import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Term } from "@/admin/types";
import {
  fetchTermByIdRemote,
  fetchTermsRemote,
} from "@/lib/termsApi";

export type { Term };

function norm(s: string): string {
  return s.trim().toLowerCase();
}

/** Portal list filtering (letter / word-only search / limit / status) applied after GET /terms/. */
export function filterPortalTerms(
  rows: Term[],
  options?: {
    letter?: string;
    search?: string;
    limit?: number;
    status?: string;
    preferFeatured?: boolean;
    /** When true, keep only terms with `featured` set (after portal status/visibility rules). */
    featuredOnly?: boolean;
  }
): Term[] {
  let list = [...rows];
  const status = (
    options?.status === "pending" || options?.status === "rejected"
      ? options.status
      : "approved"
  ) as Term["status"];
  list = list.filter(t => t.status === status);

  if (status === "approved") {
    list = list.filter(t => t.is_enabled);
  }

  if (options?.featuredOnly) {
    list = list.filter(t => t.featured);
  }

  const letterRaw = options?.letter?.trim();
  if (letterRaw) {
    const letter = norm(letterRaw)[0];
    list = list.filter(t => norm(t.word).startsWith(letter ?? ""));
  }

  const searchRaw = options?.search?.trim();
  if (searchRaw) {
    const q = norm(searchRaw);
    list = list.filter(t => norm(t.word).includes(q));
  }

  if (options?.preferFeatured) {
    list.sort((a, b) => {
      const fa = a.featured ? 1 : 0;
      const fb = b.featured ? 1 : 0;
      if (fb !== fa) {
        return fb - fa;
      }
      return norm(a.word).localeCompare(norm(b.word));
    });
  } else {
    list.sort((a, b) => norm(a.word).localeCompare(norm(b.word)));
  }

  if (options?.limit != null && options.limit > 0) {
    list = list.slice(0, options.limit);
  }

  return list;
}

/** Single portal GET `/terms/` for Browse / Search / Index / autocomplete. */
export function usePortalTermsCatalog() {
  return useQuery({
    queryKey: ["terms", "portal-api", "catalog"],
    queryFn: async (): Promise<Term[]> => {
      const { terms: rows, error } = await fetchTermsRemote("portal");
      if (error) {
        throw new Error(error);
      }
      return rows ?? [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

export function useTerms(options?: {
  letter?: string;
  search?: string;
  limit?: number;
  status?: string;
  preferFeatured?: boolean;
  featuredOnly?: boolean;
}) {
  const catalog = usePortalTermsCatalog();

  const data = useMemo(
    () =>
      catalog.data !== undefined
        ? filterPortalTerms(catalog.data, options)
        : undefined,
    [
      catalog.data,
      options?.letter,
      options?.search,
      options?.limit,
      options?.status,
      options?.preferFeatured,
      options?.featuredOnly,
    ]
  );

  return { ...catalog, data };
}

export function useTerm(id: string) {
  return useQuery({
    queryKey: ["term", "portal-api", id],
    queryFn: async (): Promise<Term> => {
      const { term, error } = await fetchTermByIdRemote(id, "portal");
      if (error) {
        throw new Error(error);
      }
      if (!term || (!term.id && !term.word)) {
        throw new Error("Term not found");
      }
      if (term.status !== "approved" || !term.is_enabled) {
        throw new Error("Term not found");
      }
      return term;
    },
    enabled: !!id.trim(),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

export function useTermsCount() {
  const catalog = usePortalTermsCatalog();

  const data = useMemo(
    () =>
      catalog.data !== undefined
        ? catalog.data.filter(t => t.status === "approved" && t.is_enabled)
          .length
        : undefined,
    [catalog.data]
  );

  return { ...catalog, data };
}
